import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowRight,
  GitCompare,
  CheckCircle,
  HelpCircle,
  BookOpen,
  Sparkles,
  ArrowUpRight,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import VideoEmbed from '../components/VideoEmbed';
import videoManifest from '../data/videos.json';
import { comparisonsData, getComparisonBySlug } from '../data/comparisonsData';

const ComparisonDetailPage = () => {
  const { slug } = useParams();
  const comparison = getComparisonBySlug(slug);

  if (!comparison) {
    return <Navigate to="/compare" replace />;
  }

  const canonical = `https://www.getsortio.com/compare/${comparison.slug}`;

  const breadcrumbItems = [
    { label: 'Compare', href: '/compare' },
    { label: comparison.shortTitle || comparison.title, href: null },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.getsortio.com/' },
      { '@type': 'ListItem', position: 2, name: 'Compare', item: 'https://www.getsortio.com/compare' },
      {
        '@type': 'ListItem',
        position: 3,
        name: comparison.shortTitle || comparison.title,
        item: canonical,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comparison.title,
    description: comparison.metaDescription,
    url: canonical,
    datePublished: comparison.lastUpdated,
    dateModified: comparison.lastUpdated,
    about: {
      '@type': 'SoftwareApplication',
      name: 'Sortio',
      url: 'https://www.getsortio.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'CMG Labs LLC',
      url: 'https://www.getsortio.com',
    },
  };

  const schemas = [breadcrumbSchema, articleSchema];

  if (comparison.faq && comparison.faq.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: comparison.faq.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    });
  }

  // Other comparisons for the related links rail (everything except the current page).
  const relatedComparisons = comparisonsData.filter((c) => c.slug !== comparison.slug);

  return (
    <div className="min-h-screen bg-black text-apple-100">
      <SEOHead
        title={comparison.seoTitle}
        description={comparison.seoDescription}
        canonical={canonical}
        schema={schemas}
        keywords={[comparison.targetKeyword, ...(comparison.secondaryKeywords || [])].join(', ')}
        lastModified={comparison.lastUpdated}
      />

      <div className="container mx-auto px-6 max-w-4xl py-16 lg:py-24 relative z-10">
        <Breadcrumbs items={breadcrumbItems} />

        <Link
          to="/compare"
          className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-8 transition-colors group"
        >
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>All comparisons</span>
        </Link>

        <div className="mb-10">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <GitCompare className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-6 text-apple-50">
            {comparison.title}
          </h1>
          <p className="text-apple-300 text-lg leading-relaxed">{comparison.metaDescription}</p>
          <p className="text-apple-400 text-sm mt-4">
            Last updated {new Date(comparison.lastUpdated).toLocaleDateString()}
          </p>
        </div>

        {videoManifest[`/compare/${slug}`] && (
          <VideoEmbed video={videoManifest[`/compare/${slug}`]} />
        )}

        {/* Intro paragraphs */}
        {comparison.intro && comparison.intro.length > 0 && (
          <section className="mb-12 space-y-5 text-apple-200 leading-relaxed">
            {comparison.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>
        )}

        {/* Comparison table */}
        {comparison.comparisonTable && (
          <section className="mb-14">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6">At a glance</h2>
            <div className="overflow-x-auto rounded-2xl border border-apple-600/30 bg-apple-700/20">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-apple-600/30">
                    {comparison.comparisonTable.headers.map((h, i) => (
                      <th
                        key={i}
                        className={`px-4 py-3 text-left font-medium ${
                          i === 0 ? 'text-apple-300' : 'text-apple-50'
                        }`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.comparisonTable.rows.map((row, r) => (
                    <tr key={r} className="border-b border-apple-600/20 last:border-0">
                      {row.map((cell, c) => (
                        <td
                          key={c}
                          className={`px-4 py-3 align-top ${
                            c === 0 ? 'text-apple-400 font-medium' : 'text-apple-200'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Per-competitor sections (pillar) */}
        {comparison.competitorSections && comparison.competitorSections.length > 0 && (
          <section className="mb-14">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6">
              The breakdown
            </h2>
            <div className="space-y-8">
              {comparison.competitorSections.map((s) => (
                <div
                  key={s.anchor}
                  id={s.anchor}
                  className="bg-apple-700/30 rounded-2xl border border-apple-600/30 p-6"
                >
                  <h3 className="text-xl font-semibold text-apple-50 mb-2">{s.name}</h3>
                  <p className="text-sortio-primary text-sm mb-4 italic">{s.verdict}</p>
                  <div className="space-y-3 text-apple-200 leading-relaxed">
                    {s.body.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                  {s.link && (
                    <Link
                      to={s.link.href}
                      className="inline-flex items-center text-sortio-primary hover:text-sortio-primary/80 mt-4 text-sm group"
                    >
                      {s.link.label}
                      <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sections (head-to-head comparisons) */}
        {comparison.sections && comparison.sections.length > 0 && (
          <section className="mb-14 space-y-10">
            {comparison.sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-2xl font-semibold text-apple-50 mb-4">{s.heading}</h2>
                <div className="space-y-4 text-apple-200 leading-relaxed">
                  {s.body.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Verdict */}
        {comparison.verdict && (
          <section className="mb-14">
            <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-8">
              <h2 className="text-2xl font-semibold text-apple-50 mb-4 flex items-center">
                <CheckCircle className="text-sortio-primary mr-3" size={24} />
                {comparison.verdict.heading}
              </h2>
              <div className="space-y-3 text-apple-200 leading-relaxed">
                {comparison.verdict.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {comparison.faq && comparison.faq.length > 0 && (
          <section className="mb-14">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
              <HelpCircle className="text-sortio-primary mr-3" size={24} />
              Frequently asked questions
            </h2>
            <div className="space-y-4">
              {comparison.faq.map((f, i) => (
                <div key={i} className="bg-apple-700/20 rounded-xl p-6">
                  <h3 className="font-medium text-apple-50 mb-3">{f.question}</h3>
                  <p className="text-apple-300 leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Next steps */}
        {comparison.nextSteps && comparison.nextSteps.length > 0 && (
          <section className="mb-14">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6 flex items-center">
              <BookOpen className="text-sortio-primary mr-3" size={24} />
              Keep reading
            </h2>
            <div className="grid gap-3">
              {comparison.nextSteps.map((s, i) => (
                <Link
                  key={i}
                  to={s.href}
                  className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
                >
                  <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                    {s.label}
                  </span>
                  <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
            <div className="flex items-center mb-4">
              <Sparkles className="text-sortio-primary mr-3" size={24} />
              <h2 className="text-2xl font-semibold text-apple-50">Try Sortio for free</h2>
            </div>
            <p className="text-apple-300 mb-6 leading-relaxed">
              The free tier includes a one-time AI trial allowance and rule-based sorting up to five rules. Enough to test on a real folder before deciding anything.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Download Sortio
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </section>

        {/* Other comparisons */}
        {relatedComparisons.length > 0 && (
          <section className="border-t border-apple-700/30 pt-8">
            <h3 className="text-lg font-medium text-apple-50 mb-4">Other comparisons</h3>
            <div className="grid gap-3">
              {relatedComparisons.map((c) => (
                <Link
                  key={c.slug}
                  to={`/compare/${c.slug}`}
                  className="text-apple-300 hover:text-sortio-primary text-sm transition-colors inline-flex items-center group"
                >
                  <ArrowRight className="w-3 h-3 mr-2 opacity-60 group-hover:opacity-100 transition-opacity" />
                  {c.shortTitle || c.title}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ComparisonDetailPage;
